import {
  Document,
  InvoiceWithDocuments,
  TableDataType,
} from '../types/invoice.types';
import { getDocuments } from '../apis/getDocuments';
import { uploadDocument } from '../apis/submitDocument';
import { AuthContextProps } from '../contexts/AuthContext';

export const transformInvoiceDataToTableData = (
  invoicesWithDocs: InvoiceWithDocuments[],
  handleDocumentSelection: (
    billNumber: string,
    documentId: string,
    isSelected: boolean
  ) => void
): TableDataType[] => {
  if (!invoicesWithDocs.length) return [];

  return invoicesWithDocs.map((invoice): TableDataType => {
    const documentsList: React.ReactNode = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {invoice.documents && invoice.documents.length > 0 ? (
          invoice.documents.map((doc: Document) => (
            <div
              key={doc.Id}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <input
                type="checkbox"
                checked={invoice.selectedDocuments.includes(doc.Id.toString())}
                onChange={(e) =>
                  handleDocumentSelection(
                    invoice.BILL_NUM.toString(),
                    doc.Id.toString(),
                    e.target.checked
                  )
                }
              />
              <span>{doc.DocumentName}</span>
            </div>
          ))
        ) : (
          <span style={{ color: 'gray', fontStyle: 'italic' }}>
            No documents
          </span>
        )}
      </div>
    );

    return {
      billNumber: invoice.BILL_NUM.toString(),
      billDate: new Date(invoice.BILL_DATE).toLocaleDateString(),
      clientCode: invoice.CLIENT_CODE,
      clientName: invoice.CLIENT_NAME,
      matterCategoryCode: invoice.MATT_CAT_CODE,
      clientMatterCode: invoice.CLNT_MATT_CODE,
      matterName: invoice.MATTER_NAME,
      documents: documentsList,
    };
  });
};

export const fetchDocumentsForInvoice = async (
  billNumber: string,
  getAccessToken: AuthContextProps['getAccessToken']
): Promise<Document[]> => {
  try {
    const response = await getDocuments({ billNumber: billNumber, getAccessToken });
    return (response.data as unknown as Document[]) || [];
  } catch (error) {
    console.error(`Error fetching documents for bill ${billNumber}:`, error);
    return [];
  }
};

export const handleFileUpload = async (
  files: File[],
  billNumber: string,
  getAccessToken: AuthContextProps['getAccessToken']
): Promise<{ success: boolean; error?: string }> => {
  try {
    const uploadPromises = files.map(async (file) => {
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const result = await uploadDocument({
        file,
        imageName: cleanFileName,
        billNumber: billNumber,
        getAccessToken,
      });

      if (!result.success) {
        throw new Error(result.error || `Failed to upload ${file.name}`);
      }

      return result;
    });

    const results = await Promise.all(uploadPromises);
    const allSuccessful = results.every(
      (result: { success: any }) => result.success
    );

    if (!allSuccessful) {
      throw new Error('Some files failed to upload');
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};
